import React, {useEffect, useState} from "react";
import {
  Box,
  Card,
  CircularProgress,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import {Trans, useTranslation} from "react-i18next";
import Grid from "@mui/material/Unstable_Grid2";
import {useSearchParams} from "react-router-dom";
import {toastError} from "../../common/utils/toastMessages";
import {
  useWorkflowExecutionMutation,
  WorkflowExecutionApiArg,
} from "../../features/workflowTriggers/workflowExecution-api";
import {useAppSelector} from "../../store";
import toast from "react-hot-toast";
import {ConfirmDeliveryProps} from "../pages/interfaces";
import {getCustomValue} from "../../../utils/helper.utils";
import {
  OrderGetOrderApiArg,
  useOrderGetOrderMutation,
} from "../../features/order/order-api";
import {getFormattedDate} from "../../../utils/formatting.utils";

export default function ConfirmDeliveryForm({
  orderId,
  trackingNumber,
}: ConfirmDeliveryProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();

  const [message, setMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [_orderId, setOrderId] = useState<number | null>(null);
  const [_trackingNumber, setTrackingNumber] = useState<string | null>(null);
  const [parcel, setParcel] = useState<any>(null);

  useEffect(() => {
    setOrderId(orderId ?? Number(searchParams.get("orderId")));
    setTrackingNumber(trackingNumber ?? searchParams.get("trackingNumber"));
  }, [orderId, trackingNumber, searchParams]);

  const confirmDeliveryWorkflowId = useAppSelector(
    (state: any) =>
      state.organizationConfigState?.workflows?.confirmDeliveryWorkflowId,
  );

  const [runWorkflow] = useWorkflowExecutionMutation();

  const confirmDelivery = async (
    orderId: number,
    trackingNumber: string,
    message?: string,
  ) => {
    const executeWorkflowApiArgs: WorkflowExecutionApiArg = {
      organizationId: process.env
        .REACT_APP_PORTAL_ORGANIZATION_ID as unknown as number,
      workflowId: confirmDeliveryWorkflowId,
      values: {
        variables: {
          orderId,
          trackingNumber,
          message,
        },
      },
    };
    const confirmDeliveryResponse = await runWorkflow(executeWorkflowApiArgs);

    const workflowError = (confirmDeliveryResponse as any)?.error;

    if (workflowError) {
      toastError(workflowError?.data?.errors?.General?.[1]);
    } else {
      toast.success(t("deliveryConfirmed"));

      getParcel(orderId).then((response: any) => {
        setDeliveryConfirmedDate(
          getCustomValue(response.data?.customValues, "deliveryConfirmedDate"),
        );
        setDeliveryConfirmedMessage(
          getCustomValue(response.data?.customValues, "deliveryNotes"),
        );
      });
    }
  };

  const confirm = async () => {
    try {
      setIsLoading(true);
      if (
        _orderId !== null &&
        _orderId !== undefined &&
        !isNaN(_orderId) &&
        _trackingNumber
      )
        await confirmDelivery(_orderId, _trackingNumber, message);
    } catch (error) {
      toastError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const [deliveryConfirmedDate, setDeliveryConfirmedDate] = useState<
    string | null
  >(null);

  const [deliveryConfirmedMessage, setDeliveryConfirmedMessage] = useState<
    string | null
  >(null);

  const [getOrder] = useOrderGetOrderMutation();

  const getParcel = async (orderId: number) => {
    const commandArgs: OrderGetOrderApiArg = {
      organizationId: process.env
        .REACT_APP_PORTAL_ORGANIZATION_ID as unknown as number,
      divisionId: process.env.REACT_APP_PORTAL_DIVISION_ID as unknown as number,
      orderId,
    };
    return getOrder(commandArgs);
  };

  useEffect(() => {
    if (_orderId) {
      getParcel(_orderId)
        .then((response: any) => {
          setParcel(response.data);
          setDeliveryConfirmedDate(
            getCustomValue(
              response.data?.customValues,
              "deliveryConfirmedDate",
            ),
          );
          setDeliveryConfirmedMessage(
            getCustomValue(response.data?.customValues, "deliveryNotes"),
          );
        })
        .finally(() => setIsLoaded(true));
    }
  }, [_orderId]);

  return isLoaded ? (
    <Card
      sx={
        isDesktop
          ? {
              border: "1px solid #D8E6FB",
              boxShadow: "0px 0px 8px rgba(15, 74, 132, 0.1)",
              borderRadius: "16px",
              marginBottom: "25px",
              marginTop: 4,
            }
          : {
              border: "none",
              boxShadow: "none",
            }
      }
    >
      {" "}
      {deliveryConfirmedDate ? (
        <>
          <Typography
            variant={"h2"}
            mb={3}
            mt={3}
            ml={isDesktop ? 3 : 0}
            color="rgb(0, 189, 117);"
          >
            <Trans i18nKey={"deliveryConfirmedDate"}>
              Delivery was confirmed on
            </Trans>
            &nbsp;{getFormattedDate(deliveryConfirmedDate)}
          </Typography>
        </>
      ) : (
        <Grid container columns={{xs: 6, md: 6}} justifyContent="center">
          <Grid
            xs={6}
            md={4}
            pb={0}
            position={"relative"}
            sx={{textAlign: "center"}}
          >
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontFamily: "Inter SemiBold",
                pt: "40px",
                mb: 2,
              }}
            >
              <Trans i18nKey="confirmDelivery1">
                We received notification for last mile carrier that parcel
              </Trans>
              &nbsp;{_trackingNumber}&nbsp;
              <Trans i18nKey="confirmDelivery2">was delivered.</Trans> <br />
              <Trans i18nKey="confirmDelivery3">Please confirm parcel</Trans>
            </Typography>

            <Grid>
              <TextField
                fullWidth
                multiline
                name="message"
                size={isDesktop ? "medium" : "small"}
                label={t("feedback")}
                placeholder={t("yourMessage") ?? ""}
                data-testid="input-feedback"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMessage(event.target.value);
                }}
              />
            </Grid>
            <Grid xs={6}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                color="secondary"
                sx={{width: "75%", mt: 2, mb: 4}}
                data-testid="btn-submit"
                disabled={isLoading}
                loadingPosition="end"
                endIcon={<></>}
                loading={isLoading}
                onClick={confirm}
              >
                <Trans i18nKey="btnConfirm">Confirm</Trans>
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Card>
  ) : (
    <Box display="flex" justifyContent="center" alignItems="center">
      <CircularProgress style={{height: "80px", width: "80px"}} />
    </Box>
  );
}
